import { Alert, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { tagNameToColor } from "@/lib/miscellaneous/tags";
import { cn } from "@/lib/utils";
import { AlertTriangle, CircleAlert, ThumbsDown, ThumbsUp } from "lucide-react";
import { useTranslation } from "react-i18next";

export type TagAlertProps = {
  tagName: string;
  tagId: string;
  isApproved: boolean;
  hasVoted: boolean;
  onVote: (tagId: string, approval: boolean) => void;
  title: string;
  message?: string;
  subMessage?: string;
  details: Record<string, { flag: boolean; reasoning: string | null }>;
};

export const TagAlert = ({
  tagName,
  tagId,
  isApproved,
  hasVoted,
  onVote,
  title,
  message,
  subMessage,
  details,
}: TagAlertProps) => {
  const alertColor = tagNameToColor(tagName);
  const { t } = useTranslation();

  return (
    <Alert
      className={cn("p-4 mb-2")}
      variant="warning"
      style={{
        backgroundColor: alertColor.backgroundColor,
        color: "black",
        borderColor: alertColor.textColor,
        borderWidth: "1px",
        borderStyle: "solid",
      }}
    >
      <AlertTitle className="font-bold flex items-center text-base">
        <AlertTriangle
          size={25}
          className="mr-2"
          style={{ color: alertColor.textColor }}
        />
        {title}
      </AlertTitle>
      <div className="p-0 m-0 mt-2">
        {message && <div className="text-sm font-medium mb-1">{message}</div>}

        <div className="space-y-1">
          {Object.entries(details).map(([name, { flag, reasoning }]) =>
            flag && reasoning ? (
              <div className="text-sm" key={name}>
                <strong>{name}</strong>:
                {reasoning && (
                  <span className="text-gray-600 ml-2">{reasoning}</span>
                )}
              </div>
            ) : null,
          )}
        </div>
        {subMessage && (
          <div className="text-sm font-medium mt-2">{subMessage}</div>
        )}
      </div>
      {!hasVoted && !isApproved && (
        <div className="flex justify-end gap-2 mt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onVote(tagId, true)}
            className="flex items-center gap-1"
          >
            <ThumbsUp size={14} />
            {t("invoiceReview.feedback.correct")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onVote(tagId, false)}
            className="flex items-center gap-1"
          >
            <ThumbsDown size={14} />
            {t("invoiceReview.feedback.wrong")}
          </Button>
        </div>
      )}
      {hasVoted && (
        <div className="flex justify-end mt-2">
          <div className="text-sm text-green-600 font-medium">
            ✓ {t("invoiceReview.feedback.feedback_submitted")}
          </div>
        </div>
      )}
    </Alert>
  );
};
